import React from "react";
import Image from "next/image";
import { HiClock } from "react-icons/hi";
import { IoFootsteps } from "react-icons/io5";
import classNames from "classnames";

//components
import Button from "@/shared/Button";
import Link from "@/components/Link";
import LocationSpaceCarousel from "@/containers/LocationSpaceCarousel";

// interfaces
import { ILocationCard } from "./ILocationCard";

//helpers
import { formatCurrency } from "@/helpers/currencyUtils";
import separatedNumber from "@/helpers/separatedNumber";

//styles
import styles from "@/assets/styles/components/location-card.module.scss";


const LocationCard = (props: ILocationCard) => {
  const { location, currency, purpose, onSpacesClick, user, setActiveLocationId } = props;

  const accessRestricted = location.accessRestricted && !user?.id;
  const {
    city,
    name,
    spaceCount,
    demographic,
    highestDailyPrice,
    lowestDailyPrice,
    pictures,
    isNew,
    newSpacesCount,
    collections,
  } = location;

  const demographicList = [
    {
      icon: <HiClock/>,
      value: `Dwell time ${separatedNumber(demographic?.dwellTime as number)} mins`,
      label: <span className={styles["locl-location-card__demographic__item__label"]}>avg. dwell time/visit</span>,
      key: "avg-dwell"
    },
    {
      icon: <IoFootsteps/>,
      value: `Footfall ${separatedNumber(demographic?.weeklyFootfall as number)}`,
      label: <span className={styles["locl-location-card__demographic__item__label"]}>footfall/week</span>,
      key: "weeklyFootfall"
    },
  ].filter(Boolean) as { icon: JSX.Element; value: string; label?: JSX.Element; key: string }[];

  const onClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    onSpacesClick();
  };

  const handleOnMouseEnter = () => {
    if (setActiveLocationId) {
      setActiveLocationId(location.id);
    }
  };

  const handleOnMouseLeave = () => {
    if (setActiveLocationId) {
      setActiveLocationId(null);
    }
  };

  // renders
  const renderBadgeNew = () => {
    switch (true) {
      case isNew:
        return (
          <div className={styles["locl-location-card__badge-new"]} data-testid="locl-location-card-badge-new">
            <span>New location</span>
          </div>
        );
      case newSpacesCount && newSpacesCount > 0:
        return (
          <div className={styles["locl-location-card__badge-new"]} data-testid="locl-location-card-badge-new">
            <span>{newSpacesCount} new space{newSpacesCount as number > 1 && "s"}</span>
          </div>
        );
      default:
        return null;
    }
  }

  const renderPrice = () => {
    if (!lowestDailyPrice || !highestDailyPrice) {
      return "Price on application";
    }

    return `From ${formatCurrency(lowestDailyPrice[currency?.code], currency?.code)}/day`;
  }

  const renderCollectionBox = (text: string) => (
    <div className={styles["locl-location-card__collections__box"]}>
      { text }
    </div>
  )

  const isFestivalLocation = location.collections.some(collection => collection.name === "Festivals");

  return (
    <Link
      href={`/location/${location.slug}${purpose ? `?purpose__in=${purpose}` : ""}`}
      className={styles["locl-location-card__wrap"]}
      data-testid="locl-location-card-wrap"
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    >
      <div className={styles["locl-location-card__image-wrapper"]}>
        {renderBadgeNew()}
        <div className={styles["locl-location-card__city"]}>{city}</div>
        <LocationSpaceCarousel
          className={styles["locl-location-card__image"]}
          items={(pictures || []).map((img) => ({
            src: img.thumbnailImageSecond ? img.thumbnailImageSecond : img.image,
            previewSrc: img.image
          }))}
          slidesPerView={1}
          imageStyle={{ filter: `${accessRestricted ? "blur(5px)" : "none"}` }}
        >
          {isFestivalLocation && location?.logoUrl && (
            <Image
              width={274}
              height={130}
              loading="lazy"
              src={location.logoUrl as string}
              style={{ filter: `${accessRestricted ? "blur(5px)" : "none"}` }}
              alt={location.name}
              className={styles["locl-location-card__thumbnail"]}/>
          )}
        </LocationSpaceCarousel>
      </div>
      <div className={styles["locl-location-card__info"]}>
        <span
          className={classNames(
            styles["locl-location-card__name"],
            { [styles["locl-location-card__name__restricted"]]: accessRestricted }
          )}
          data-testid="locl-location-card-name"
        >
          {name}
        </span>
        <div className={styles["locl-location-card__price"]} data-testid="locl-location-card-price">
          { renderPrice() }
        </div>
        <div className={styles["locl-location-card__demographic"]}>
          {demographicList.map((el) => (
            <div key={el.key} className={styles["locl-location-card__demographic__item"]} data-testid={`locl-location-card-${el.key}`}>
              {el.icon}
              <span className={styles["locl-location-card__demographic__item__value"]} data-testid={`locl-location-card-${el.key}-value`}>{el.value}</span>
            </div>)
          )}
        </div>
      </div>
      <div className={styles["locl-location-card__footer"]}>
        <div className={styles["locl-location-card__collections"]}>
          { collections?.[0] && renderCollectionBox(collections[0].name) }
          { collections.length > 1 && renderCollectionBox(`+${collections.length - 1}`) }
        </div>
        <Button
          type="secondary"
          className={styles["locl-location-card__show-spaces-button-link"]}
          onClick={onClick}
          data-testid="locl-location-card-btn"
        >
          {spaceCount} Space{spaceCount > 1 && "s"}
        </Button>
      </div>
    </Link>
  );
};

export default LocationCard;
